import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMail, FiLinkedin, FiCheck, FiCalendar } from 'react-icons/fi';
import { personalInfo } from '../../data/portfolioData';
import CardSpotlight from '../CardSpotlight/CardSpotlight';
import styles from './Contact.module.css';

const fadeUpVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

const Contact = () => {
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(personalInfo.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    
    setStatus('loading');
    
    try {
      const response = await fetch('https://portfolio-send-email-api.vercel.app/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          subject: `${formData.name} reached out to you from Portfolio Website!`,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('idle'), 4000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 4000);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  return (
    <section id="contact" className={styles.contactSection}>
      <div className={styles.container}>
        <motion.div 
          className={styles.header}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUpVariants}
        >
          <span className={styles.label}>GET IN TOUCH</span>
          <h2 className={styles.h2}>Let's Build Something Together</h2>
          <p className={styles.subtitle}>
            Available for freelance projects, full-time remote roles, and AI collaboration.
          </p>
        </motion.div>

        <motion.div 
          className={styles.cardsRow}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          {/* Email Card */}
          <motion.div variants={fadeUpVariants} style={{ height: '100%' }}>
            <CardSpotlight className={styles.contactCard}>
              <FiMail size={40} className={styles.icon} />
              <h3 className={styles.cardTitle}>Email Me</h3>
              <p className={styles.cardValue}>{personalInfo.email}</p>
              <div className={styles.cardActions}>
                <button className={styles.ghostBtn} onClick={handleCopyEmail}>
                  {copied ? <><FiCheck /> Copied!</> : 'Copy Email'}
                </button>
                <a href={`mailto:${personalInfo.email}`} className={styles.ghostBtn}>
                  Send Email →
                </a>
              </div>
            </CardSpotlight>
          </motion.div>

          {/* LinkedIn Card */}
          <motion.div variants={fadeUpVariants} style={{ height: '100%' }}>
            <CardSpotlight className={styles.contactCard}>
              <FiLinkedin size={40} className={styles.icon} />
              <h3 className={styles.cardTitle}>Connect on LinkedIn</h3>
              <p className={styles.cardValue}>linkedin.com/in/usman-mahmood-1b54851a0</p>
              <div className={styles.cardActions}>
                <a href={personalInfo.linkedin} target="_blank" rel="noreferrer" className={styles.ghostBtn}>
                  View Profile →
                </a>
              </div>
            </CardSpotlight>
          </motion.div>

          {/* Book a Meeting Card */}
          <motion.div variants={fadeUpVariants} style={{ height: '100%' }}>
            <CardSpotlight className={styles.contactCard}>
              <FiCalendar size={40} className={styles.icon} />
              <h3 className={styles.cardTitle}>Book a Meeting</h3>
              <p className={styles.cardValue}>calendly.com/usmanx191</p>
              <div className={styles.cardActions}>
                <a href="https://calendly.com/usmanx191/new-meeting" target="_blank" rel="noreferrer" className={styles.ghostBtn}>
                  Schedule Now →
                </a>
              </div>
            </CardSpotlight>
          </motion.div>
        </motion.div>

        {/* Contact Form */}
        <motion.div 
          className={styles.formContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUpVariants}
        >
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputGroup}>
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. John Doe" 
                className={styles.input} 
                required 
              />
            </div>
            <div className={styles.inputGroup}>
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="e.g. john@company.com" 
                className={styles.input} 
                required 
              />
            </div>
            <div className={styles.inputGroup}>
              <textarea 
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Hi Usman, I'd like to collaborate on an AI project..." 
                rows="4" 
                className={styles.textarea}
                required
              ></textarea>
            </div>
            <button 
              type="submit" 
              className={styles.submitBtn}
              disabled={status === 'loading' || status === 'success'}
              style={{
                opacity: status === 'loading' ? 0.7 : 1,
                cursor: status === 'loading' || status === 'success' ? 'not-allowed' : 'pointer',
                backgroundColor: status === 'success' ? '#22c55e' : status === 'error' ? '#ef4444' : undefined,
                color: (status === 'success' || status === 'error') ? '#fff' : undefined
              }}
            >
              {status === 'loading' ? 'Sending...' : 
               status === 'success' ? 'Message Sent Successfully ✓' : 
               status === 'error' ? 'Failed to Send ✗' : 
               'Send Message →'}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
